// src/services/workspace-ai-service.ts
// AI service for the Workspace - handles chat, section operations, and analysis

import { supabase } from '../lib/supabase';

// ============================================================
// TYPES
// ============================================================

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
}

export interface ChatContext {
  proposalId: string;
  proposalTitle: string;
  contractAgency: string;
  currentSectionId?: string;
  currentSectionTitle?: string;
  currentSectionContent?: string;
  companyName?: string;
}

export interface ProposalData {
  contract?: any;
  contentJson?: any;
  gapReport?: any;
  winAssessment?: any;
  extractedRequirements?: any[];
}

export interface AIResponse {
  content: string;
  suggestions?: string[];
  actions?: Array<{
    type: 'fix' | 'navigate' | 'add-to-profile';
    label: string;
    data?: any;
  }>;
}

export interface SectionRegenerationRequest {
  proposalId: string;
  sectionId: string;
  sectionTitle: string;
  currentContent: string;
  instruction?: string;
  contract: {
    title: string;
    agency: string;
    description?: string;
  };
}

export interface SectionEnhancementRequest {
  proposalId: string;
  sectionId: string;
  content: string;
  enhancementType: 'concise' | 'evidence' | 'compliance' | 'simplify' | 'terminology';
  context?: {
    contract?: any;
    profile?: any;
  };
}

// ============================================================
// AI CHAT SERVICE
// ============================================================

const CHAT_SYSTEM_PROMPT = `You are an expert federal proposal consultant integrated into BidWin, a proposal management workspace. You have deep knowledge of:

1. Federal acquisition regulations (FAR/DFARS)
2. Government proposal best practices
3. Evaluation criteria and scoring
4. Pricing strategies for government contracts
5. Compliance requirements and certifications

Context about the current proposal will be provided. Answer questions helpfully and provide actionable advice.

Key guidelines:
- Be specific and actionable in your recommendations
- Reference FAR/DFARS clauses when relevant
- Suggest concrete improvements when possible
- If you don't know something, say so rather than guessing
- Keep responses concise but thorough`;

export class WorkspaceAIService {
  private conversationHistory: ChatMessage[] = [];
  private context: ChatContext | null = null;
  private proposalData: ProposalData | null = null;

  /**
   * Initialize the service with proposal context
   */
  setContext(context: ChatContext) {
    this.context = context;
    this.conversationHistory = [];
  }

  /**
   * Set full proposal data for rich context
   */
  setProposalData(data: ProposalData) {
    this.proposalData = data;
  }

  /**
   * Update the current section being viewed
   */
  updateSection(sectionId: string, sectionTitle: string, sectionContent?: string | any) {
    if (this.context) {
      this.context.currentSectionId = sectionId;
      this.context.currentSectionTitle = sectionTitle;
      // Handle both string content and object content (like deliverables data)
      if (typeof sectionContent === 'string') {
        this.context.currentSectionContent = sectionContent.substring(0, 2000);
      } else if (sectionContent && typeof sectionContent === 'object') {
        // Convert object to summary string for context
        this.context.currentSectionContent = JSON.stringify(sectionContent).substring(0, 2000);
      } else {
        this.context.currentSectionContent = undefined;
      }
    }
  }

  /**
   * Send a chat message and get AI response
   */
  async sendMessage(userMessage: string): Promise<AIResponse> {
    // Add user message to history
    this.conversationHistory.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    });

    try {
      // Call AI endpoint with full context
      const { data, error } = await supabase.functions.invoke('workspace-chat', {
        body: {
          messages: this.conversationHistory.map(m => ({
            role: m.role,
            content: m.content,
          })),
          context: this.context,
          proposalData: this.proposalData,
        },
      });

      if (error) {
        console.error('Workspace chat error:', error);
        // Fallback to simulated response if endpoint doesn't exist
        return this.generateFallbackResponse(userMessage);
      }

      const aiResponse = data.response || data.content || data.message;

      // Add assistant response to history
      this.conversationHistory.push({
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString(),
      });

      return {
        content: aiResponse,
        suggestions: data.suggestions,
        actions: data.actions,
      };
    } catch (err) {
      console.error('Chat error:', err);
      // Return fallback response
      return this.generateFallbackResponse(userMessage);
    }
  }

  /**
   * Build context prompt based on current state
   */
  private buildContextPrompt(): string {
    if (!this.context) return '';

    let prompt = `\n\nCURRENT CONTEXT:\n`;
    prompt += `- Proposal: "${this.context.proposalTitle}"\n`;
    prompt += `- Agency: ${this.context.contractAgency}\n`;

    if (this.context.companyName) {
      prompt += `- Company: ${this.context.companyName}\n`;
    }

    if (this.context.currentSectionTitle) {
      prompt += `- Currently viewing: ${this.context.currentSectionTitle}\n`;
    }

    if (this.context.currentSectionContent) {
      prompt += `\nCURRENT SECTION CONTENT (excerpt):\n${this.context.currentSectionContent}\n`;
    }

    return prompt;
  }

  /**
   * Generate fallback response when AI endpoint unavailable
   */
  private generateFallbackResponse(userMessage: string): AIResponse {
    const query = userMessage.toLowerCase();

    // Pattern matching for common questions
    if (query.includes('improve') || query.includes('better')) {
      return {
        content: `To improve this section, I'd recommend:

1. **Add Specific Metrics**: Include concrete numbers, percentages, and dollar amounts where possible. Evaluators respond well to quantifiable achievements.

2. **Strengthen Compliance Language**: Use "shall" instead of "will" for mandatory requirements. Reference specific solicitation sections.

3. **Include Past Performance**: Cite relevant prior contracts with similar scope, especially same-agency experience.

4. **Address Evaluation Criteria**: Make sure each evaluation factor is explicitly addressed in your response.

Would you like me to help with any of these specific areas?`,
        suggestions: [
          'Add metrics to strengthen claims',
          'Review compliance language',
          'Add past performance references',
        ],
      };
    }

    if (query.includes('price') || query.includes('cost') || query.includes('rate')) {
      return {
        content: `For federal pricing, here are key considerations:

1. **Labor Rates**: Should be based on GSA rates, DOL wage determinations, or market research. Rates significantly above GSA schedules need justification.

2. **Indirect Rates**: Fringe (typically 25-35%), Overhead (40-100%), and G&A (10-20%) should be defensible and based on actual costs.

3. **Profit/Fee**: FFP contracts typically allow 10-15% profit. T&M and cost-reimbursable have lower fee ceilings per FAR.

4. **Price-to-Win**: Research the incumbent's pricing and similar contract awards through USASpending.gov.

What specific pricing questions can I help with?`,
        suggestions: [
          'Help calculate loaded rates',
          'Review my indirect rates',
          'Find comparable contract pricing',
        ],
      };
    }

    if (query.includes('missing') || query.includes('gap') || query.includes('weak')) {
      return {
        content: `Based on my analysis, here are common proposal gaps to check:

1. **Requirement Coverage**: Ensure every mandatory requirement (shall statement) is explicitly addressed.

2. **Certification Compliance**: Verify you have all required certifications (e.g., CMMC, ISO, etc.) or have a plan to obtain them.

3. **Past Performance**: Need at least 3 relevant past performance references within the last 5 years.

4. **Key Personnel**: Resumes should show direct relevant experience and required certifications.

5. **Technical Approach**: Should demonstrate understanding of the agency's specific challenges.

The Gap Resolution panel shows specific issues found in your proposal. Would you like me to explain any of them in detail?`,
      };
    }

    if (query.includes('why') || query.includes('explain')) {
      return {
        content: `I'd be happy to explain the AI's reasoning. The proposal content is generated based on:

1. **Your Company Profile**: Capabilities, past performance, certifications, and key personnel inform what we can credibly claim.

2. **Contract Requirements**: The solicitation's evaluation criteria, SOW, and compliance requirements drive the structure.

3. **Federal Best Practices**: Language and format follow FAR guidelines and proven proposal strategies.

4. **Evidence Matching**: Each claim is traced to documented evidence from your profile when possible.

What specific aspect would you like me to explain further?`,
      };
    }

    // Default response
    return {
      content: `I'm here to help with your proposal. I can assist with:

- **Section Analysis**: Understanding strengths and weaknesses
- **Compliance**: FAR/DFARS requirements and certifications
- **Pricing**: Rate calculations and price-to-win analysis
- **Technical Approach**: Methodology and solution design
- **Past Performance**: Relevance and presentation

What would you like help with?`,
      suggestions: [
        'How can I improve this section?',
        'What requirements am I missing?',
        'Help me with pricing',
      ],
    };
  }

  /**
   * Clear conversation history
   */
  clearHistory() {
    this.conversationHistory = [];
  }

  /**
   * Get conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }
}

// ============================================================
// SECTION OPERATIONS
// ============================================================

/**
 * Regenerate a proposal section using AI
 */
export async function regenerateSection(
  request: SectionRegenerationRequest
): Promise<{ content: string; success: boolean }> {
  try {
    const { data, error } = await supabase.functions.invoke('regenerate-section', {
      body: {
        proposalId: request.proposalId,
        sectionId: request.sectionId,
        sectionTitle: request.sectionTitle,
        currentContent: request.currentContent,
        instruction: request.instruction,
        contract: request.contract,
      },
    });

    if (error) throw error;

    return {
      content: data.content || data.newContent,
      success: true,
    };
  } catch (err) {
    console.error('Section regeneration error:', err);
    throw err;
  }
}

/**
 * Enhance a section with specific improvements
 */
export async function enhanceSection(
  request: SectionEnhancementRequest
): Promise<{ content: string; success: boolean }> {
  const enhancementPrompts: Record<string, string> = {
    concise: 'Make this more concise while preserving key points. Federal evaluators review many proposals.',
    evidence: 'Add more specific evidence, metrics, and past performance references to support claims.',
    compliance: 'Strengthen compliance language. Use "shall" for requirements, reference specific FAR clauses.',
    simplify: 'Simplify for non-technical evaluators while maintaining accuracy.',
    terminology: 'Align terminology with the solicitation language and agency preferences.',
  };

  try {
    const { data, error } = await supabase.functions.invoke('enhance-deliverable-section', {
      body: {
        content: request.content,
        enhancementType: request.enhancementType,
        instruction: enhancementPrompts[request.enhancementType],
        context: request.context,
      },
    });

    if (error) throw error;

    return {
      content: data.enhancedContent || data.content,
      success: true,
    };
  } catch (err) {
    console.error('Section enhancement error:', err);
    throw err;
  }
}

/**
 * Fix a specific issue in a section
 */
export async function fixSectionIssue(
  proposalId: string,
  sectionId: string,
  issueId: string,
  issueDescription: string,
  currentContent: string
): Promise<{ content: string; success: boolean }> {
  try {
    // For now, use the regenerate endpoint with a fix instruction
    const { data, error } = await supabase.functions.invoke('regenerate-section', {
      body: {
        proposalId,
        sectionId,
        currentContent,
        instruction: `Fix this issue: ${issueDescription}. Keep other content intact.`,
      },
    });

    if (error) throw error;

    return {
      content: data.content || data.newContent,
      success: true,
    };
  } catch (err) {
    console.error('Issue fix error:', err);
    throw err;
  }
}

// ============================================================
// SINGLETON INSTANCE
// ============================================================

let chatServiceInstance: WorkspaceAIService | null = null;

export function getWorkspaceAIService(): WorkspaceAIService {
  if (!chatServiceInstance) {
    chatServiceInstance = new WorkspaceAIService();
  }
  return chatServiceInstance;
}
